import React, { Suspense, useMemo } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickAutocompleteInput from "@/features/pathpick/components/ui/PathPickAutocompleteInput";
import useGetPathPickBasketFilterInfo from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketFilterInfo";
import usePathPickBasketFilter from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import { toPathPickBrandOptions } from "@/features/pathpick/utils";

interface Props {
  // 입력창 글자 — 헤더가 검색 버튼에 쓰는 사본을 갱신한다.
  onChange: (value: string) => void;
  // Enter — 자동완성 항목을 고르지 않은 채 친 검색어로 검색한다.
  onSubmit: () => void;
}

// 브랜드 목록을 받기 전에는 범위가 비어 있는 것으로 보고 제안하지 않는다.
const EMPTY_BRAND_SCOPE: number[] = [];

// 담은 상품 검색창 — 상품 찾기와 같은 자동완성을 담은 상품 범위로 좁혀 붙인다.
// 자동완성 API는 패스픽 전체 기준이라 담지 않은 브랜드·SKU 도 내려준다. 담은 상품 필터 정보의 브랜드를
// 제안 범위로 넘겨, 브랜드 제안은 담은 브랜드만 남기고 SKU 제안은 담은 브랜드로 좁힌다
// (같은 브랜드의 담지 않은 SKU 는 서버가 담은 상품 범위를 몰라 여전히 섞일 수 있다).
function PathPickBasketSearchInput(props: Props) {
  // 브랜드 목록(서스펜스 쿼리)을 기다리는 동안 검색창이 사라지지 않게, 범위가 빈 같은 검색창을 먼저 보인다.
  // 같은 헤더의 필터 패널이 같은 키로 이미 조회하므로 대개 바로 넘어간다.
  return (
    <Suspense
      fallback={
        <BasketAutocompleteInput {...props} brandScope={EMPTY_BRAND_SCOPE} />
      }
    >
      <ScopedBasketAutocompleteInput {...props} />
    </Suspense>
  );
}

function ScopedBasketAutocompleteInput(props: Props) {
  const { pathPickBasketFilterInfo } = useGetPathPickBasketFilterInfo();
  // 필터 패널의 브랜드 옵션과 같은 기준 — 고른 브랜드가 필터 칩·패널에서 해제할 수 있는 값이 되게.
  const brandScope = useMemo(
    () =>
      toPathPickBrandOptions(pathPickBasketFilterInfo?.brands).map(
        (brand) => brand.id
      ),
    [pathPickBasketFilterInfo?.brands]
  );

  return <BasketAutocompleteInput {...props} brandScope={brandScope} />;
}

function BasketAutocompleteInput({
  brandScope,
  onChange,
  onSubmit,
}: Props & { brandScope: number[] }) {
  const { t } = useI18nHelper();
  const { filter, apply, applyQuery } = usePathPickBasketFilter();

  // 브랜드를 고르면 브랜드 필터에 더하고 검색어는 비운다 — 검색어까지 남기면
  // "브랜드 AND 검색어"로 좁혀져 고른 브랜드의 상품이 안 보인다.
  const handleSelectBrand = (brandId: number) => {
    const current = filter.brandId__in ?? [];
    apply({
      ...filter,
      brandId__in: current.includes(brandId) ? current : [...current, brandId],
      query: undefined,
    });
  };

  // SKU 를 고르면 그 값으로 바로 검색한다(원본 SKU — 서버가 기호를 지우고 찾는다).
  const handleSelectSku = (sku: string) => {
    applyQuery(sku);
  };

  return (
    <PathPickAutocompleteInput
      width={"100%"}
      externalValue={filter.query}
      brandIds={filter.brandId__in}
      brandScope={brandScope}
      onChange={onChange}
      onSubmit={onSubmit}
      onSelectBrand={handleSelectBrand}
      onSelectSku={handleSelectSku}
      placeholder={t("path_pick:basket.list.search_placeholder")}
      inputProps={{
        border: "0.5px solid #C0C0C0",
        _hover: {
          borderColor: "#1272EF",
        },
        height: "32px",
        // 모바일 키보드의 엔터 키를 `검색`으로 보여준다 — 모바일엔 검색 버튼이 없다.
        enterKeyHint: "search",
      }}
    />
  );
}

export default PathPickBasketSearchInput;
